import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ActionsDropdown } from "@/components/ui/actions-dropdown";
import { ArrowLeft, Edit, Copy, Trash2, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import * as policiesApi from "@/services/policiesApi";
import { fromPolicy } from "@/components/software-updates/wizard/serialize";
import { MODE_META, SU_PLATFORM_META } from "@/components/software-updates/wizard/types";
import { summarize } from "@/components/software-updates/wizard/lib/summary";

export default function SoftwareUpdatePolicyDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ["policy", id],
    queryFn: () => policiesApi.getPolicy(id),
    enabled: !!id,
  });

  const del = useMutation({
    mutationFn: () => policiesApi.deletePolicy(id),
    onSuccess: () => {
      toast.success("Software update policy deleted");
      qc.invalidateQueries({ queryKey: ["policies"] });
      navigate("/software-updates?tab=policies");
    },
    onError: () => toast.error("Failed to delete software update policy"),
  });

  if (isLoading) {
    return (
      <div className="px-6 py-6 flex items-center text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading policy…
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="px-6 py-6 space-y-3">
        <Button variant="ghost" size="sm" onClick={() => navigate("/software-updates?tab=policies")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back
        </Button>
        <p className="text-muted-foreground">Couldn't load this policy.</p>
      </div>
    );
  }

  const state = fromPolicy(data);
  const lines = summarize(state);

  return (
    <div className="px-6 py-6 space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-2 min-w-0">
          <Button variant="ghost" size="sm" onClick={() => navigate("/software-updates?tab=policies")}>
            <ArrowLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" aria-hidden="true" />
            {data.name}
          </h1>
          <div className="flex flex-wrap gap-1.5">
            <Badge variant="secondary">{MODE_META[state.mode].label}</Badge>
            <Badge variant="secondary">{SU_PLATFORM_META[state.platform].label}</Badge>
          </div>
          {data.description ? (
            <p className="text-sm text-muted-foreground max-w-2xl">{data.description as string}</p>
          ) : null}
        </div>
        <ActionsDropdown
          actions={[
            { label: "Edit", icon: Edit, onClick: () => navigate(`/software-updates/policies/${id}/edit`) },
            {
              label: "Duplicate",
              icon: Copy,
              onClick: () => navigate(`/software-updates/policies/new?duplicate=${id}`),
            },
            {
              label: "Delete",
              icon: Trash2,
              destructive: true,
              onClick: () => setConfirmOpen(true),
            },
          ]}
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Configuration</CardTitle>
          <CardDescription>What this policy enforces on {SU_PLATFORM_META[state.platform].label}.</CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="space-y-1.5 text-sm">
            {lines.map((l, i) => (
              <li key={i} className="text-muted-foreground">• {l}</li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Software Update Policy</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &ldquo;{data.name}&rdquo;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={(e) => {
                e.preventDefault();
                del.mutate();
              }}
              disabled={del.isPending}
            >
              {del.isPending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
